import { useState, useEffect } from "react";
import { getIn, useFormikContext } from "formik";
import { useRenterDashboardContext } from "@/modules/renters/components/renter-dashboard/useRenterDashboardContext";
import { getWorkAndProducts } from "@/common/services/maintenance.api";

import { DamagedPartRow, FormValues } from "@/types/repossession.type";
import { budgetItem, Budgetlabel, emptyBudgetLabel } from "@/types/maintenance.type";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { VEHICLE_STATUS } from "@/types/vehicle.type";
import { unlinkVehicleRenter } from "@/common/services/renters.api";
import { updateVehicleVisualRecords } from "@/common/services/vehicles.api";

export const useInspectionRegister = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registers: any[],
  onCloseModal?: () => void,
  manualCollision?: boolean
) => {
  const { renter } = useRenterDashboardContext();
  const formik = useFormikContext<FormValues>();
  const { notifySuccess, notifyError } = useToastContext();
  const COLLISION_THRESHOLD = 1500;

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const damagedItems: Budgetlabel[] = [
    emptyBudgetLabel(),
  ];
  const [cachedOptions, setCachedOptions] = useState<{ value: budgetItem, label: string }[]>([]);
  const [damagedPartsRows, setDamagedPartsRows] = useState<DamagedPartRow[]>([
    { id: crypto.randomUUID(), selectedOption: null, quantity: 1 },
  ]);

  // Valores do Formik
  const vehicle = getIn(formik.values, "vehicle");
  const renterId = getIn(formik.values, "renterId");
  const nameRenter = getIn(formik.values, "renterName");
  const licensePlate = getIn(formik.values, "licensePlate");
  const optionRepossesion =
    getIn(formik.values, "optionRepossesion") || "UNDEFINED";
  const changeVehicle = getIn(formik.values, "changeVehicle");

  // Effects
  useEffect(() => {
    setDisableButton(
      optionRepossesion === "UNDEFINED" || typeof changeVehicle !== "boolean"
    );
  }, [changeVehicle, optionRepossesion]);

  useEffect(() => {
    if (!formik.values.damagedParts) {
      formik.setFieldValue("damagedParts", damagedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers de peças avariadas
  const addPartRow = () => {
    setDamagedPartsRows((prev) => [
      ...prev,
      { id: crypto.randomUUID(), selectedOption: null, quantity: 1 },
    ]);
  };

  const removePartRow = (index: number) => {
    setDamagedPartsRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePartRow = (
    index: number,
    field: keyof DamagedPartRow,
    value: number | string
  ) => {
    setDamagedPartsRows((prev) => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], [field]: value };
      return newRows;
    });
  };

  // Carregar opções de peças
  const loadOptions = async (inputValue: string) => {
    try {
      if (cachedOptions.length === 0) {
        const response = await getWorkAndProducts();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formattedOptions = response.data.map((item: any) => ({
          value: item._id,
          label: `${item.name} - (R$ ${Number(item.sellPrice || 0).toFixed(2)})`,
          originalData: item,
        }));
        setCachedOptions(formattedOptions);

        return inputValue
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? formattedOptions.filter((option: any) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
          )
          : formattedOptions;
      }

      const filteredOptions = inputValue
        ? cachedOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
        : cachedOptions;

      return filteredOptions;
    } catch (error) {
      console.error("Erro ao buscar peças", error);
      return [];
    }
  };

  // Resolver peças selecionadas
  const resolveSelectedItems = () => {
    return damagedPartsRows
      .filter((row) => row.selectedOption)
      .map((row) => ({
        name: row.selectedOption.originalData?.name || row.selectedOption.label,
        code: row.selectedOption.originalData?.code || "",
        productOrWorkId:
          row.selectedOption.value ||
          row.selectedOption.originalData?._id ||
          "",
        quantity: row.quantity,
        sellPrice: row.selectedOption.originalData?.sellPrice || 0,
      }));
  };

  // Gerar texto das peças avariadas
  const getDamagedPartsText = (
    parts: ReturnType<typeof resolveSelectedItems>
  ) => {
    if (!parts.length) return "";
    return [
      "\nPeças Avariadas:",
      ...parts.map(
        (part) =>
          `- ${part.quantity}x ${part.name} (R$ ${Number(part.sellPrice).toFixed(2)})`
      ),
    ].join("\n");
  };

  // Calcular valor total das peças avariadas
  const calculateDamagedPartsTotal = (parts: ReturnType<typeof resolveSelectedItems>) => {
    return parts.reduce((acc, part) => {
      const qty = Number(part.quantity ?? 1) || 1;
      const price = Number(part.sellPrice ?? 0) || 0;
      return acc + qty * price;
    }, 0);
  };

  // Handler principal de desvinculação
  const handleUnlinkVehicle = async () => {
    const currentReason = getIn(formik.values, "returnReason") || "";
    const withoutPhotos = getIn(formik.values, "withoutPhotos") || false;
    const hasMissingPhotos = !withoutPhotos && registers.some((register) => !register?.content?.trim());
    const hasInvalidReason = withoutPhotos && !currentReason.trim();
    const hasInvalidReasonType = optionRepossesion === "UNDEFINED";
    const hasInvalidChangeVehicle = typeof changeVehicle !== "boolean";

    if (
      hasInvalidReasonType ||
      hasInvalidChangeVehicle ||
      hasInvalidReason ||
      hasMissingPhotos
    ) {
      if (hasMissingPhotos) {
        notifyError("Envie todas as fotos obrigatórias do veículo antes de continuar.");
      } else {
        notifyError("Preencha os campos obrigatórios antes de continuar.");
      }
      setDisableButton(false);
      return;
    }

    setDisableButton(true);
    setIsLoading(true);

    try {
      const resultDamagedParts = resolveSelectedItems();

      const newReturnReason = `${currentReason}${getDamagedPartsText(
        resultDamagedParts
      )}`;

      formik.setFieldValue("damagedParts", resultDamagedParts);
      formik.setFieldValue("returnReason", newReturnReason);

      const plan = renter?.activationData?.plan?.label;
      let availableForRent = true;

      if (plan?.includes("SV") && changeVehicle === true) {
        availableForRent = false;
      }


      const savePhotoDelivery = {
        renterId: renterId,
        vehicleId: vehicle._id,
        photos: withoutPhotos ? [] : registers,
        nameRenter: nameRenter,
        licensePlate: licensePlate,
        damagedParts: resultDamagedParts,
        returnReason: newReturnReason,
        withoutPhotos: withoutPhotos,
      };

      const resultPhoto = await updateVehicleVisualRecords(savePhotoDelivery);

      if (!resultPhoto.ok) {
        notifyError(resultPhoto.message as string);
        setDisableButton(false);
        return;
      }

      notifySuccess("Fotos salvas com sucesso");
      // Extrair ID do registro visual do veículo
      const photoData = (resultPhoto.data || {}) as {
        _id?: string;
        vehicleVisualRecordId?: string;
        visualRecordId?: string;
        data?: {
          _id?: string;
          vehicleVisualRecordId?: string;
          visualRecordId?: string;
        };
      };
      const vehicleVisualRecordId =
        photoData._id ||
        photoData.vehicleVisualRecordId ||
        photoData.visualRecordId ||
        photoData.data?._id ||
        photoData.data?.vehicleVisualRecordId ||
        photoData.data?.visualRecordId ||
        "";

      if (!vehicleVisualRecordId) {
        notifyError("Não foi possível identificar o registro visual da devolução.");
        setDisableButton(false);
        return;
      }
      
      const totalPiecesValue = calculateDamagedPartsTotal(resultDamagedParts);
      const autoCollision = totalPiecesValue >= COLLISION_THRESHOLD;
      const finalCollision = Boolean(manualCollision) || autoCollision;

      const unlinkData = {
        renterId: renterId,
        vehicleId: vehicle._id,
        newStatusVehicle: VEHICLE_STATUS.TRIAGE,
        optionRepossesion: optionRepossesion,
        damagedParts: resultDamagedParts,
        observation: formik.values.observation,
        availableForRent: availableForRent,
        returnReason: newReturnReason,
        withoutPhotos: withoutPhotos,
        changeVehicle,
        collision: finalCollision,
        vehicleVisualRecordId,
        visualRecordId: vehicleVisualRecordId,
      };

      const resultUnlinkVehicle = await unlinkVehicleRenter(unlinkData);

      if (!resultUnlinkVehicle.ok) {
        notifyError(
          resultUnlinkVehicle.message || "Não foi possível desvincular o veículo."
        );
        setDisableButton(false);
        return;
      }

      notifySuccess("Veículo desvinculado com sucesso");

      // Reset dos registros
      formik.setFieldValue("registers", [
        { type: "Frente do veículo", content: "" },
        { type: "Lateral direita do veículo", content: "" },
        { type: "Lateral esquerda do veículo", content: "" },
        { type: "Traseira do veículo", content: "" },
        { type: "Odômetro", content: "" },
      ]);
      formik.setFieldValue("withoutPhotos", false);

      if (onCloseModal) {
        onCloseModal();
      }
    } catch (error) {
      console.error("Erro ao processar operações:", error);
      notifyError("Ocorreu um erro ao processar a operação");
      setDisableButton(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler para adicionar registro de fotos
  const handleAddRegister = () => {
    formik.setFieldValue("registers", [
      ...registers,
      { type: "", content: "", verified: false },
    ]);
  };

  return {
    // Estados
    isLoading,
    disableButton,
    damagedPartsRows,

    // Valores do Formik
    vehicle,
    renterId,
    nameRenter,
    licensePlate,

    // Handlers de peças
    addPartRow,
    removePartRow,
    updatePartRow,
    loadOptions,

    // Handler principal
    handleUnlinkVehicle,
    handleAddRegister,
  };
};
